import { LANES } from '@accra/shared';
import { TRAFFIC_KINDS, VARIANTS_PER_KIND } from '../../content/traffic';
import { STOP_LINE_OFFSET } from '../../content/junctions';
import type { Rng } from '../../core/rng';
import type { OncomingCar, RunState } from '../../game/RunState';
import { junctionAt, nextJunction, signalAt } from '../junctions/signals';
import { inQuietZone } from '../uturn/uTurn';

/** The other carriageway: cars driving towards us. Scenery only, never part of any hit test. */
export const ONCOMING = {
  /** How many are on the road at once. */
  count: 9,
  /** They appear this far ahead (min, max) and go once this far behind. */
  appearAhead: [140, 300] as const,
  goneBehind: 40,
  /** Nearest two in a lane keep at least this gap, metres. */
  minGap: 16,
  followGap: 6,
  accel: 3,
  brake: 7,
} as const;

const KINDS = TRAFFIC_KINDS.filter((kind) => !kind.hoppable);
const AMBER_COMMIT = 9;
/** They start braking for a red light this far out. */
const RED_LOOKAHEAD = 45;

interface Leader {
  gap: number;
  speed: number;
}

/** The lights at junctions work for them like they do for us: red means wait at the line. */
function lightAhead(car: OncomingCar, time: number): Leader | null {
  // They drive towards smaller s, so the junction they meet next is the one just behind them (in our driving order).
  const above = nextJunction(car.s);
  const candidates = above.seq > 0 ? [above, junctionAt(above.seq - 1)] : [above];
  for (const point of candidates) {
    const line = point.position + STOP_LINE_OFFSET;
    const toLine = car.s - car.kind.length / 2 - line;
    if (toLine < -0.5 || toLine > RED_LOOKAHEAD) continue;
    const signal = signalAt(point.seq, time);
    if (signal === 'green' || (signal === 'amber' && toLine < AMBER_COMMIT)) continue;
    return { gap: toLine, speed: 0 };
  }
  return null;
}

function leaderOf(car: OncomingCar, all: readonly OncomingCar[], time: number): Leader | null {
  let best = lightAhead(car, time);
  for (const other of all) {
    if (other === car || other.lane !== car.lane || other.s >= car.s) continue;
    const gap = car.s - other.s - (car.kind.length + other.kind.length) / 2;
    if (!best || gap < best.gap) best = { gap, speed: other.speed };
  }
  return best;
}

/** Keeps the far carriageway busy: cars come at us, queue at red lights and go by. */
export class OncomingFlow {
  private nextId = 1;

  constructor(private readonly rng: Rng) {}

  update(state: RunState, dt: number): void {
    const { bus } = state;
    state.oncoming = state.oncoming.filter((car) => car.s > bus.s - ONCOMING.goneBehind);
    // Near the end of the road, where the trotro turns round, no new ones set off.
    if (!inQuietZone(bus.s) && !state.turn.active) this.fill(state);
    for (const car of state.oncoming) this.drive(car, state, dt);
  }

  private fill(state: RunState): void {
    let guard = 12;
    while (state.oncoming.length < ONCOMING.count && guard-- > 0) {
      const kind = this.rng.pick(KINDS);
      const lane = this.rng.int(0, LANES - 1);
      const s = state.bus.s + this.rng.range(ONCOMING.appearAhead[0], ONCOMING.appearAhead[1]);
      if (inQuietZone(s) || state.oncoming.some((other) => other.lane === lane && Math.abs(other.s - s) < ONCOMING.minGap)) continue;
      const cruise = this.rng.range(kind.speed[0], kind.speed[1]) * 1.4;
      state.oncoming.push({ id: this.nextId++, kind, variant: this.rng.int(0, VARIANTS_PER_KIND - 1), lane, s, speed: cruise, cruise });
    }
  }

  private drive(car: OncomingCar, state: RunState, dt: number): void {
    const leader = leaderOf(car, state.oncoming, state.stats.seconds);
    let wanted = car.cruise;
    if (leader && leader.gap < ONCOMING.followGap) wanted = Math.min(wanted, leader.speed * Math.max(0, leader.gap / ONCOMING.followGap));
    // A red light further off: roll up to the line and stop there.
    if (leader && leader.speed === 0 && leader.gap >= ONCOMING.followGap) wanted = Math.min(wanted, Math.sqrt(2 * ONCOMING.brake * 0.6 * Math.max(0, leader.gap - 1)));
    const change = wanted > car.speed ? ONCOMING.accel * dt : -ONCOMING.brake * dt;
    car.speed = Math.max(0, wanted > car.speed ? Math.min(wanted, car.speed + change) : Math.max(wanted, car.speed + change));
    car.s -= car.speed * dt;
  }
}
